import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  IconButton,
  Paper,
  Popper,
  TextField,
  Typography,
  autocompleteClasses,
  createFilterOptions,
  useMediaQuery,
} from "@mui/material";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import TrainIcon from "@mui/icons-material/Train";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";

import { Location } from "../../../@types/DTO/Locations";
import LocationDetailPanel from "./LocationDetailPanel";
import LocationDetailsDialog from "./LocationDetailsDialog";
import { pushRecentLocation, readRecentLocations } from "./recentLocations";
import { isTruthyLocationFlag } from "./locationFormatters";

type Section = "cronologia" | "popolari" | "all";

const POPULAR_LIMIT = 6;

const POPUP_MIN_WIDTH_PX = 520;
const DESKTOP_BREAKPOINT_PX = 900;

const CLICK_AWAY_GRACE_MS = 150;

export interface LocationSelectWithPanelProps {
  value: string;
  onChange: (value: string) => void;
  options: Location[];
  getOptionValue: (location: Location) => string;
  getOptionLabel?: (location: Location) => string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  anchorDate?: Date | null;
  recentKey: string;
  disableSoftKeyboard?: boolean;
}

interface OptionEntry {
  location: Location;
  optionValue: string;
  optionLabel: string;
  section: Section;
}

const filterOptionsIncludingAddress = createFilterOptions<OptionEntry>({
  stringify: (opt) =>
    [
      opt.optionLabel,
      opt.location.locationName,
      opt.location.locationCity,
      opt.location.zipCode,
      opt.location.locationAddress,
      opt.location.locationCode,
    ]
      .filter(Boolean)
      .join(" "),
});

function iconForLocation(l: Location) {
  if (isTruthyLocationFlag(l.isAirport))
    return <FlightTakeoffIcon htmlColor="blue" />;
  if (isTruthyLocationFlag(l.isRailway)) return <TrainIcon htmlColor="green" />;
  return <LocationCityIcon color="primary" />;
}

const PanelContext = React.createContext<{
  hovered: Location | null;
  anchorDate: Date | null | undefined;
  onOpenDetails: () => void;
  isDesktop: boolean;
} | null>(null);

const PaperWithPanel = React.forwardRef<HTMLDivElement, any>(
  function PaperWithPanel(props, ref) {
    const ctx = React.useContext(PanelContext);
    const isDesktop = ctx?.isDesktop ?? false;
    return (
      <Paper
        ref={ref}
        {...props}
        sx={{
          display: "flex",
          alignItems: "stretch",
          gap: 0,
          overflow: "hidden",
          ...(isDesktop ? { minWidth: POPUP_MIN_WIDTH_PX } : {}),
        }}
      >
        <Box sx={{ flex: "1 1 auto", minWidth: 240 }}>{props.children}</Box>
        {isDesktop && ctx?.hovered && (
          <Box
            sx={{
              flex: "0 0 320px",
              borderLeft: 1,
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <LocationDetailPanel
              location={ctx.hovered}
              anchorDate={ctx.anchorDate}
              onOpenDetails={ctx.onOpenDetails}
            />
          </Box>
        )}
      </Paper>
    );
  }
);

const WidePopper = (props: any) => {
  const ctx = React.useContext(PanelContext);
  const isDesktop = ctx?.isDesktop ?? false;
  return (
    <Popper
      {...props}
      style={{
        ...(props.style || {}),
        ...(isDesktop
          ? { width: undefined, minWidth: POPUP_MIN_WIDTH_PX }
          : {}),
      }}
      placement="bottom-start"
    />
  );
};

const LocationSelectWithPanel: React.FC<LocationSelectWithPanelProps> = ({
  value,
  onChange,
  options,
  getOptionValue,
  getOptionLabel,
  placeholder,
  label,
  required,
  disabled,
  className,
  anchorDate,
  recentKey,
  disableSoftKeyboard,
}) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(`(min-width: ${DESKTOP_BREAKPOINT_PX}px)`, {
    defaultMatches:
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT_PX}px)`).matches,
  });

  const [hovered, setHovered] = useState<Location | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLocation, setDrawerLocation] = useState<Location | null>(null);
  const [recentCodes, setRecentCodes] = useState<string[]>([]);
  const [popupOpen, setPopupOpen] = useState(false);
  const drawerOpenRef = React.useRef(false);

  // Load history once per mount / when the field key changes.
  useEffect(() => {
    setRecentCodes(readRecentLocations(recentKey));
  }, [recentKey]);
  const entries: OptionEntry[] = useMemo(() => {
    const recentSet = new Set(recentCodes);
    return (options || []).map((l) => {
      const section: Section = recentSet.has(l.locationCode)
        ? "cronologia"
        : isTruthyLocationFlag(l.isAirport) || isTruthyLocationFlag(l.isRailway)
        ? "popolari"
        : "all";
      return {
        location: l,
        optionValue: getOptionValue(l),
        optionLabel: (getOptionLabel || getOptionValue)(l),
        section,
      };
    });
  }, [options, recentCodes, getOptionValue, getOptionLabel]);

  const sortedEntries: OptionEntry[] = useMemo(() => {
    const byCode = new Map(entries.map((e) => [e.location.locationCode, e]));
    const cronologia = recentCodes
      .map((c) => byCode.get(c))
      .filter((e): e is OptionEntry => Boolean(e));
    const popolari = entries
      .filter((e) => e.section === "popolari")
      .slice(0, POPULAR_LIMIT);
    const all = entries
      .filter((e) => e.section === "all")
      .sort((a, b) => a.optionLabel.localeCompare(b.optionLabel));
    return [...cronologia, ...popolari, ...all];
  }, [entries, recentCodes]);

  const selected: OptionEntry | null = useMemo(() => {
    if (!value) return null;
    return (
      sortedEntries.find(
        (e) => e.optionValue.toLowerCase() === value.toLowerCase()
      ) || null
    );
  }, [value, sortedEntries]);

  useEffect(() => {
    setHovered(selected ? selected.location : null);
  }, [selected]);

  const sectionLabel = (s: Section): string => {
    if (s === "cronologia") return t("History");
    if (s === "popolari") return t("Popular stations");
    return t("All stations");
  };

  const openDrawerFor = useCallback((location: Location) => {
    drawerOpenRef.current = true;
    setDrawerLocation(location);
    setDrawerOpen(true);
    setPopupOpen(true);
  }, []);

  const onOpenDetails = useCallback(() => {
    if (!hovered) return;
    openDrawerFor(hovered);
  }, [hovered, openDrawerFor]);

  const panelContextValue = useMemo(
    () => ({ hovered, anchorDate, isDesktop, onOpenDetails }),
    [hovered, anchorDate, isDesktop, onOpenDetails]
  );

  return (
    <PanelContext.Provider value={panelContextValue}>
      <Autocomplete<OptionEntry, false, false, false>
        className={className}
        disabled={disabled}
        options={sortedEntries}
        value={selected}
        open={popupOpen}
        onOpen={() => setPopupOpen(true)}
        onClose={() => {
          if (drawerOpenRef.current) return;
          setPopupOpen(false);
        }}
        sx={{
          "& .MuiAutocomplete-clearIndicator": { display: "none" },
        }}
        onChange={(_, next) => {
          if (next) {
            onChange(next.optionValue);
            pushRecentLocation(recentKey, next.location.locationCode);
            setRecentCodes(readRecentLocations(recentKey));
          } else {
            onChange("");
          }
        }}
        onHighlightChange={(_, opt) => {
          setHovered(opt ? opt.location : selected?.location || null);
        }}
        getOptionLabel={(opt) => opt.optionLabel || ""}
        filterOptions={filterOptionsIncludingAddress}
        isOptionEqualToValue={(a, b) =>
          a?.location?.locationCode === b?.location?.locationCode
        }
        groupBy={(opt) => sectionLabel(opt.section)}
        renderGroup={(params) => (
          <li key={params.key}>
            <Box
              sx={{
                px: 2,
                py: 1,
                fontWeight: 700,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                color: "text.secondary",
                position: "sticky",
                top: -8,
                backgroundColor: "background.paper",
              }}
            >
              {params.group}
            </Box>
            <ul style={{ padding: 0 }}>{params.children}</ul>
          </li>
        )}
        renderOption={(props, opt) => (
          <li {...props} key={opt.location.locationCode}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                py: 0.25,
              }}
            >
              {iconForLocation(opt.location)}
              <Typography
                sx={{ flex: 1, fontSize: "1rem" }}
                component="span"
                noWrap
              >
                {opt.optionLabel || opt.location.locationName}
              </Typography>
              {!isDesktop && (
                <IconButton
                  size="small"
                  edge="end"
                  aria-label={t("Location details")}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    openDrawerFor(opt.location);
                  }}
                  sx={{ ml: 0.5 }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            required={required}
            variant="outlined"
            fullWidth
            inputProps={{
              ...params.inputProps,
              readOnly: disableSoftKeyboard || undefined,
              inputMode: disableSoftKeyboard ? "none" : undefined,
            }}
          />
        )}
        PopperComponent={WidePopper}
        PaperComponent={PaperWithPanel}
        componentsProps={{
          paper: { elevation: 8 } as any,
        }}
        ListboxProps={{
          sx: {
            [`& .${autocompleteClasses.option}`]: {
              paddingY: "6px",
            },
          },
        }}
      />
      <LocationDetailsDialog
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setPopupOpen(true);
          window.setTimeout(() => {
            drawerOpenRef.current = false;
          }, CLICK_AWAY_GRACE_MS);
        }}
        location={drawerLocation}
        anchorDate={anchorDate}
      />
    </PanelContext.Provider>
  );
};

export default LocationSelectWithPanel;
